import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { VipPlan, PaymentResponse } from '../types';
import { apiService } from '../services/api';
import { Loading } from '../components';
import { EmailVerificationModal } from '../components/EmailVerificationModal';
import airwallexConfig from '../config/airwallex';
import { init } from '@airwallex/components-sdk';

import { formatDate, parseServerDate } from '../utils/date';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CreditCard, ShieldCheck, ChevronRight, AlertCircle, Loader2, Tag } from 'lucide-react';

// Temporarily disabled: no mail server is configured yet, so nobody can ever receive
// the verification link and this gate would block all payments. Re-enable once
// transactional email sending is wired up.
const REQUIRE_EMAIL_VERIFICATION_BEFORE_PAYMENT = false;

const PaymentPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('planId');
  const { user, refreshUser } = useAuth();
  const { showAlert } = useAlert();
  const [vipPlan, setVipPlan] = useState<VipPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [paymentResponse, setPaymentResponse] = useState<PaymentResponse | null>(null);
  const [error, setError] = useState('');
  const [noPlans, setNoPlans] = useState(false);
  const [airwallexPayments, setAirwallexPayments] = useState<any>(null);
  const [sdkInitFailed, setSdkInitFailed] = useState(false);
  const [hasPendingPayment, setHasPendingPayment] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState<any>(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false);

  useEffect(() => {
    if (!user) {
      showAlert({
        type: 'warning',
        message: t('vipPlan.warningLogin'),
      });
      navigate('/login-form', { replace: true });
      return;
    }
    if (user.role !== 'EMPLOYER') {
      showAlert({
        type: 'warning',
        message: t('vipPlan.warningEmployer'),
      });
      navigate('/home', { replace: true });
      return;
    }

    // 檢查用戶是否已經是 VIP 且未過期
    if (user.isVip && user.vipExpiryDate) {
      const expiryDate = parseServerDate(user.vipExpiryDate);
      const now = new Date();
      if (expiryDate > now) {
        showAlert({
          type: 'info',
          message: t('vipPlan.infoVipActive', { date: formatDate(expiryDate) }),
        });
        navigate('/subscription', { replace: true });
        return;
      }
    }
    
    // 檢查是否有未完成的支付
    checkPendingPayments();
    
    if (planId) {
      loadPlan();
    } else {
      // 沒有指定方案：有可購方案就轉去選購頁，完全沒有方案則顯示敬請期待
      apiService.vip.getActivePlans()
        .then((res: any) => {
          if (res?.success && res.data?.length > 0) {
            navigate('/subscription', { replace: true });
          } else {
            setNoPlans(true);
          }
        })
        .catch(() => setNoPlans(true))
        .finally(() => setLoadingPlan(false));
    }
  }, [planId, user]);
  
  const checkPendingPayments = async () => {
    if (!user?.id) return;

    try {
      const response = await apiService.payments.getUserPayments(user.id);
      if (response.success && response.data) {
        const pendingPayments = response.data.filter(
          (payment: any) => payment.status === 'PENDING' || payment.status === 'PROCESSING'
        );

        if (pendingPayments.length > 0) {
          setHasPendingPayment(true);
          showAlert({
            type: 'warning',
            message: t('payment.pendingPayment'),
          });
        }
      }
    } catch (error) {
      console.error('Error checking pending payments:', error);
    }
  };

  const loadPlan = async () => {
    if (!planId) return;
    try {
      setLoadingPlan(true);
      const response = await apiService.vip.getPlanById(parseInt(planId));
      if (response.success && response.data) {
        setVipPlan(response.data);
      } else {
        setError(t('payment.errorLoadPlan'));
        showAlert({
          type: 'error',
          message: t('payment.errorLoadPlan'),
        });
      }
    } catch (error: any) {
      console.error('Error loading plan:', error);
      setError(t('payment.errorLoadPlan'));
      showAlert({
        type: 'error',
        message: t('payment.errorLoadPlan'),
      });
    } finally {
      setLoadingPlan(false);
    }
  };

  useEffect(() => {
    const initializeAirwallex = async () => {
      try {
        const env = airwallexConfig.environment === 'production' ? 'prod' : airwallexConfig.environment;
        const { payments } = await init({
          env: env as 'demo' | 'staging' | 'dev' | 'prod',
          enabledElements: ['payments'],
        });
        
        setAirwallexPayments(payments);
      } catch (error) {
        console.error('Failed to initialize Airwallex SDK:', error);
        setError(t('payment.errorInitSdk'));
        setSdkInitFailed(true);
      }
    };

    initializeAirwallex();
  }, []);

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    setPromoResult(null);
    try {
      const res = await apiService.promo.validate(promoCode.trim(), vipPlan?.id);
      if (res.success && res.data) {
        setPromoResult(res.data);
      } else {
        setPromoError(res.message || t('payment.promoInvalid', '優惠碼無效'));
      }
    } catch (e: any) {
      setPromoError(e?.response?.data?.message || t('payment.promoInvalid', '優惠碼無效'));
    } finally {
      setPromoLoading(false);
    }
  };

  const createPaymentRequest = async () => {
    if (!user?.id || !vipPlan) return;

    if (user.isVip && user.vipExpiryDate) {
      const expiryDate = parseServerDate(user.vipExpiryDate);
      const now = new Date();
      if (expiryDate > now) {
        showAlert({
          type: 'info',
          message: t('vipPlan.infoVipActive', { date: formatDate(expiryDate) }),
        });
        navigate('/subscription');
        return;
      }
    }

    // The Airwallex SDK loads asynchronously on mount; the pay button is disabled
    // until it's ready, so this should only trip if init failed outright.
    if (!airwallexPayments) {
      setError(t('payment.errorSdkNotInit'));
      showAlert({ type: 'error', message: t('payment.errorSdkNotInit') });
      return;
    }

    // Client-side guard mirroring the backend's PAYMENT_ALREADY_PENDING check — a
    // user with an unresolved payment must finish or wait it out (it auto-expires
    // after 12 hours) before starting another one.
    if (hasPendingPayment) {
      showAlert({ type: 'warning', message: t('payment.pendingPayment') });
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await apiService.payments.create(user.id, {
        vipPlanId: vipPlan.id,
        promoCode: promoResult ? promoCode.trim() : undefined,
      });

      if (response.success && response.data) {
        setPaymentResponse(response.data);

        if (response.data.paymentIntentId && response.data.paymentUrl) {
          try {
            airwallexPayments.redirectToCheckout({
              intent_id: response.data.paymentIntentId,
              client_secret: response.data.paymentUrl,
              currency: response.data.currency || 'HKD',
              country_code: 'HK',
              success_url: window.location.origin + '/payment/result',
              cancel_url: window.location.origin + '/payment/result?status=cancelled',
            });
          } catch (error: any) {
            console.error('Failed to redirect using SDK:', error);
            setError(t('payment.errorRedirect') + '：' + (error.message || ''));
            showAlert({
              type: 'error',
              message: t('payment.errorRedirect'),
            });
          }
          return;
        }

        showAlert({
          type: 'success',
          message: 'Payment request created',
        });
      } else {
        const errorMsg = response.message || t('payment.errorCreatePayment');
        setError(errorMsg);
        showAlert({
          type: 'error',
          message: errorMsg,
        });
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || t('payment.errorCreatePayment');
      setError(errorMsg);
      showAlert({
        type: 'error',
        message: errorMsg,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayment = async () => {
    if (!user?.id || !vipPlan) return;

    // Require email verification before payment
    if (REQUIRE_EMAIL_VERIFICATION_BEFORE_PAYMENT && (!user.email || !user.emailVerified)) {
      setShowEmailVerifyModal(true);
      return;
    }

    await createPaymentRequest();
  };

  const handleCheckPaymentStatus = async () => {
    if (!paymentResponse?.paymentIntentId) return;

    try {
      const response = await apiService.payments.getStatus(paymentResponse.paymentIntentId);
      if (response.success && response.data) {
        const status = response.data.status;
        if (status === 'COMPLETED') {
          await refreshUser();
          showAlert({
            type: 'success',
            message: t('payment.successPayment'),
          });
          navigate('/home');
        } else if (status === 'FAILED') {
          setError(t('payment.failedPayment'));
          showAlert({
            type: 'error',
            message: t('payment.failedPayment'),
          });
        } else {
          showAlert({
            type: 'info',
            message: t('payment.statusMessage', { status }),
          });
        }
      }
    } catch (error: any) {
      console.error('Error checking payment status:', error);
      setError(t('payment.errorCheckStatus'));
      showAlert({
        type: 'error',
        message: t('payment.errorCheckStatus'),
      });
    }
  };

  const formatPrice = (price: number, currency: string) => {
    const symbol = currency === 'HKD' ? 'HK$' : currency === 'USD' ? 'US$' : '$';
    return `${symbol}${price.toLocaleString()}`;
  };

  const formatDuration = (days: number) => {
    if (days >= 365) {
      const years = Math.floor(days / 365);
      return t('vipPlan.durationYears', { count: years });
    } else if (days >= 30) {
      const months = Math.floor(days / 30);
      return t('vipPlan.durationMonths', { count: months });
    } else {
      return t('vipPlan.durationDays', { count: days });
    }
  };

  if (loadingPlan) {
    return (
      <div className="w-full max-w-6xl mx-auto min-h-[60vh] flex items-center justify-center">
        <Loading message={t('common.loading')} />
      </div>
    );
  }

  if (noPlans) {
    return (
      <div className="w-full max-w-6xl mx-auto px-4 py-12 min-h-[60vh] flex items-center justify-center">
        <p className="text-muted-foreground text-lg">{t('payment.noPlans', '敬請期待我們的VIP方案。')}</p>
      </div>
    );
  }

  if (error && !vipPlan) {
    return (
      <div className="w-full max-w-6xl mx-auto px-4 py-12 min-h-[60vh] flex flex-col items-center justify-center">
        <div className="text-destructive font-medium mb-6 text-lg">{error}</div>
        <Button onClick={() => navigate('/subscription')} variant="default" size="lg">{t('payment.backToPlans')}</Button>
      </div>
    );
  }

  return (
    <>
    <EmailVerificationModal
      open={showEmailVerifyModal}
      currentEmail={user?.email}
      onVerified={async () => {
        const freshUser = await refreshUser?.();
        if (!freshUser?.emailVerified) {
          // User hasn't actually clicked the link in the email yet -
          // keep the modal open with a clear message instead of bouncing back silently.
          showAlert({
            type: 'warning',
            message: t('payment.emailNotVerifiedYet', '尚未完成電郵驗證，請先點擊電郵中的連結完成驗證，再按「已驗證，繼續付款」'),
          });
          return;
        }
        setShowEmailVerifyModal(false);
        await createPaymentRequest();
      }}
      onClose={() => setShowEmailVerifyModal(false)}
    />
    <div className="bg-background min-h-screen text-foreground p-4 sm:p-8">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('payment.title')}</h1>
            <p className="text-muted-foreground text-sm mt-1">{t('payment.subtitle', 'Complete your payment securely.')}</p>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-full text-xs font-semibold shadow-sm">
              <ShieldCheck className="w-3 h-3" /> {t('payment.step1')}
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground hidden sm:block" />
            <div className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-full text-xs font-semibold shadow-sm">
              <CreditCard className="w-3 h-3" /> {t('payment.step2')}
            </div>
          </div>
        </div>

      <Card className="max-w-xl mx-auto shadow-xl border-border relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary to-emerald-400" />
        
        <CardContent className="p-6 sm:p-10 pt-10">
          {vipPlan && (
            <div className="bg-muted rounded-xl p-6 mb-8 border border-border">
              <h3 className="text-lg font-bold text-foreground mb-6 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-500" /> {t('payment.planInfo')}
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-border">
                  <span className="text-muted-foreground font-medium text-sm">{t('payment.planName')}</span>
                  <span className="font-semibold text-foreground">{vipPlan.name}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-border">
                  <span className="text-muted-foreground font-medium text-sm">{t('payment.duration')}</span>
                  <span className="font-semibold text-foreground">{formatDuration(vipPlan.durationDays)}</span>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className="text-muted-foreground font-medium text-sm">{t('payment.total')}</span>
                  <div className="text-right">
                    {promoResult && (
                      <div className="text-sm text-muted-foreground line-through">
                        {formatPrice(vipPlan.price, vipPlan.currency)}
                      </div>
                    )}
                    <span className="text-3xl font-extrabold text-primary">
                      {formatPrice(promoResult ? promoResult.finalAmount : vipPlan.price, vipPlan.currency)}
                    </span>
                    {promoResult && (
                      <div className="text-sm text-emerald-600 font-medium">
                        -{formatPrice(promoResult.discountAmount, vipPlan.currency)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Promo code input */}
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoResult(null); setPromoError(''); }}
                      placeholder={t('payment.promoPlaceholder', '輸入優惠碼')}
                      className="flex-1 px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleValidatePromo}
                      disabled={promoLoading || !promoCode.trim()}
                      className="shrink-0"
                    >
                      {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
                    </Button>
                  </div>
                  {promoResult && (
                    <p className="text-xs text-emerald-600 mt-1 font-medium">{t('payment.promoApplied', { code: promoResult.code })}</p>
                  )}
                  {promoError && (
                    <p className="text-xs text-destructive mt-1">{promoError}</p>
                  )}
                </div>
                {vipPlan.description && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <span className="text-muted-foreground font-medium text-sm block mb-1">{t('payment.description')}</span>
                    <span className="text-foreground text-sm leading-relaxed">{vipPlan.description}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm mb-6 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {paymentResponse ? (
            <div className="mb-6 border border-border bg-card rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-foreground mb-4">{t('payment.paymentInfo')}</h3>
              <div className="space-y-3 mb-6 text-sm text-foreground">
                <p className="flex justify-between"><span className="text-muted-foreground">{t('payment.status')}</span> <span className="font-semibold">{paymentResponse.status}</span></p>
                <p className="flex justify-between"><span className="text-muted-foreground">{t('payment.orderId')}</span> <span className="font-mono text-xs">{paymentResponse.airwalletOrderId || paymentResponse.id}</span></p>
                
                {paymentResponse.paymentIntentId && paymentResponse.paymentUrl && (
                  <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-200 rounded-lg text-center flex flex-col items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin mb-2 text-blue-600 dark:text-blue-400" />
                    <p className="font-medium">{t('payment.redirecting')}</p>
                    <p className="text-xs text-blue-600/80 dark:text-blue-400/80 mt-2">{t('payment.redirectNote')}</p>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-3">
                <Button variant="outline" onClick={handleCheckPaymentStatus} className="w-full py-6">
                  {t('payment.checkStatus')}
                </Button>
                <Button variant="ghost" onClick={() => navigate('/subscription')} className="w-full">
                  {t('payment.backToPlans')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4 mt-8">
              <Button
                onClick={handleCreatePayment}
                disabled={loading || hasPendingPayment || (!airwallexPayments && !sdkInitFailed)}
                size="lg"
                className="w-full py-6 text-lg font-bold shadow-md hover:shadow-lg transition-all"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" /> {t('payment.processing')}
                  </>
                ) : hasPendingPayment ? (
                  t('payment.pendingPayment')
                ) : !airwallexPayments && !sdkInitFailed ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" /> {t('payment.initializingSdk', '付款服務準備中...')}
                  </>
                ) : t('payment.createPayment')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/subscription')}
                className="w-full py-5 font-semibold"
              >
                {t('payment.cancel')}
              </Button>

              <div className="flex items-center justify-center gap-2 mt-6 text-xs text-muted-foreground font-medium">
                <ShieldCheck className="w-4 h-4" /> {t('payment.securePayment')}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
    </>
  );
};

export default PaymentPage;
